import {
  EVENT_TYPE_NAMES,
  liveFrameSchema,
  liveNonActivityFrameSchema,
  ulidSchema,
  type LiveFrame,
  type LiveNonActivityFrame,
} from '@molt/shared';
import type { QueryClient } from '@tanstack/react-query';
import { eventBlastRadius, queryKeys, type MoltQueryKey } from './query-keys';

/**
 * The live board socket — `FE-04.16`.
 *
 * The substrate half of `FR-COLLAB-025`: one connection per board, kept open
 * across reconnects, turning what the server pushes into cache invalidation.
 * It renders nothing, mounts nothing and imports no React. `FE-13.1`'s board
 * face is the first intended caller (see {@link BoardSocketClient.subscribe});
 * `FE-14.x`'s card back is the second, on the same connection.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THREE THINGS THE ITEM TEXT SAYS THAT THE WIRE DOES NOT DO
 * ---------------------------------------------------------------------------
 * `FE-04.16` was written as *"subscribe one room per board against B03's
 * `molt:board:<ulid>` channel … and deliver deltas into the query cache"*.
 * Every clause of that is wrong about the transport that actually shipped, and
 * a client written from the sentence rather than the code would be subtly,
 * silently broken. What the code says instead, with the line that decided it:
 *
 *  1. **The browser never names `molt:board:<ulid>`.** That is a *server-side*
 *     Redis pub/sub channel (`boardChannel()`, `packages/shared/src/events.ts`)
 *     — the fan-out's internal address, not a client-visible one. The browser
 *     connects to the bare path `GET /ws?boardId=<ulid>`, registered in
 *     `apps/server/src/ws/board-socket.ts` and proxied by `vite.config.ts`'s
 *     `'/ws'` entry. Note it is **not** under `/api`, so nothing here can reuse
 *     `./api`'s `BASE`.
 *  2. **There is no subscribe frame, so "a room" *is* "a socket".** Both ids
 *     are fixed at the upgrade, from the query string, and
 *     `board-socket.ts` deliberately registers **no `'message'` handler for
 *     subscription at all** — "a client that wants a different card opens a
 *     different socket". So subscribing means opening a socket and
 *     unsubscribing means closing it; there is no vocabulary in which to say
 *     anything else.
 *  3. **A frame is not a delta and must not be applied as one.**
 *     `packages/shared/src/live.ts` says it outright: a frame says *that*
 *     something changed and *what kind* of change it was, `payload` is
 *     `Record<string, unknown>` by design, its contents are per-type, informal
 *     and part of no schema, and `FR-COLLAB-025` AC4's "the board reconciles to
 *     current state" is satisfied **by refetching `GET /boards/:id/contents`**.
 *     So this module invalidates; it never writes a payload into a cached
 *     object. `setQueryData` from a frame would be inventing a row out of an
 *     informal blob — the reconciliation that *is* real is `FE-18.1`'s, against
 *     optimistic local state, and it is Phase 1's last item for that reason.
 *
 * ---------------------------------------------------------------------------
 * WHY INVALIDATE-ON-RECONNECT IS LOAD-BEARING RATHER THAN TIDY
 * ---------------------------------------------------------------------------
 * **There is no replay.** No `?since=`, no last-event-id, no sequence number —
 * `live.ts` and `board-socket.ts` both refuse all three explicitly, on the
 * grounds that a client which missed frames reconciles by reading rather than
 * by rewinding. The consequence for this file is sharp: **on reconnect the
 * client learns nothing whatsoever about what it missed, and the gap has no
 * observable width.** A board that was mutated by somebody else during a
 * fifteen-second network blip would otherwise sit stale until `staleTime`
 * expired *and* something refetched — with nothing red anywhere. So
 * {@link BoardSocketClient} sweeps the board's entries after every successful
 * reconnect, and that sweep is deliberately **coarser** than a frame's:
 *
 *   - a frame → `eventBlastRadius(frame)`, the precise pair of prefixes it
 *     names;
 *   - a reconnect → the board prefix **plus `['cards']`**, because the contents
 *     of the gap are unknowable and `eventBlastRadius` has no frame to work
 *     from. `['boards', boardId]` as a prefix reaches `…/contents` and
 *     `…/lists` (and `['lists', listId, 'cards']` *through* contents, per
 *     `query-keys.ts`'s recorded limit) but cannot reach `['cards', cardId]` —
 *     a card back mounted across the blip would stay stale forever. TanStack
 *     Query only refetches **active** queries on invalidation, so `['cards']`
 *     costs one refetch per card back actually on screen — in practice one —
 *     and merely marks the rest stale.
 *
 * ---------------------------------------------------------------------------
 * ONE SOCKET PER BOARD, REFCOUNTED — AND WHY THE CARD FILTER IS NOT USED
 * ---------------------------------------------------------------------------
 * A board face and a card back on the same board are two callers wanting the
 * same stream. Opening two sockets would double the server's room membership
 * and every frame's delivery for nothing, so subscriptions are **refcounted by
 * board id**: the second `subscribe()` for a board joins the live connection,
 * and the socket closes when the last caller leaves.
 *
 * ⚠ That refcount is only *possible* because this client never sends B31's
 * optional `&cardId=<ulid>`. A card-room socket receives a **subset** —
 * the frames naming that card plus the frames naming no card — so a board room
 * and a card room are not interchangeable, and keying the refcount by board
 * alone while opening a filtered socket would starve the board face of exactly
 * the frames it exists to show. The board room is a strict superset, so one of
 * them serves both callers. **A caller that genuinely needs the filtered room
 * — `FE-31.1`'s mirror notice, `B61`'s typing indicators, which are the only
 * things that can be *sent* on one — must open its own socket rather than
 * teaching this one a `cardId` option.** `?cardId=` with an empty value is
 * refused with 1008, so "omit the parameter" is the only spelling of "no
 * filter": `board-socket.ts` compares `=== undefined` rather than falsiness
 * precisely so that mistake is loud.
 *
 * ---------------------------------------------------------------------------
 * 1008 IS TERMINAL, AND IS DELIBERATELY AMBIGUOUS
 * ---------------------------------------------------------------------------
 * An unauthorised subscriber, an unreadable board, a malformed id and a
 * mid-session revocation all arrive the same way: **close code 1008**, never an
 * HTTP refusal and never an error frame. `board-socket.ts` says why in as many
 * words — "a client cannot tell a revoked subscription from a refused one and
 * does not have to". Reconnecting after one is therefore pointless: the second
 * attempt is refused for the same reason as the first, and a backoff loop
 * against it is a permanent low-grade DoS on one's own server. So 1008 stops
 * the connection and surfaces as the terminal {@link BoardSubscriptionStatus}
 * `'refused'`, which the caller can act on (`FE-13.1` shows the board as no
 * longer live; `B31` owns any banner).
 *
 * Because that status is ambiguous by design, {@link BoardSocketClient.subscribe}
 * validates the board id **synchronously** before opening anything: a typo'd id
 * would otherwise come back as 1008 and make a caller announce lost access to a
 * member who never lost any.
 *
 * ⚠ **HOW A REFUSED BOARD RECOVERS.** A refused connection stays in the
 * registry with no socket, no timer and no retry of its own — but **a new
 * `subscribe()` for that board reopens it**, and every handle already on it is
 * carried along by the status change. That asymmetry is the whole design.
 * Retrying on a timer is pointless, because nothing has changed between one
 * tick and the next; a *new subscriber* is the one thing that is new evidence
 * somebody wants this board, and it costs exactly one refused connection to
 * find out. Without it a mid-session revocation is unrecoverable while mounted:
 * `board-socket.ts:647` closes a re-permissioned member with the same 1008, and
 * a board face does not unmount when its socket dies, so the board would stay
 * silently stale until a reload — correct over a refresh, dead over the socket,
 * with nothing red anywhere. Tearing every handle down still works too: the
 * refcount hitting zero disposes the entry outright.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS FILE IS NOT
 * ---------------------------------------------------------------------------
 *  - **No presence, no typing indicators, and no client→server send path.** The
 *    only client message the server accepts is
 *    `liveClientTypingMessageSchema`, it is valid on a card-room socket only,
 *    and it belongs to `B61`.
 *  - **No liveness timer.** `board-socket.ts` runs a server-side ping/pong
 *    heartbeat; pong is answered by the browser's own WebSocket implementation,
 *    below anything script can observe. A dead connection reaches this file as
 *    `close`, which is all the reconnect path needs.
 *  - **No React.** No hook, no provider, no context. A caller holds the handle
 *    for as long as it wants the subscription and calls `unsubscribe()` in its
 *    cleanup. The `QueryClient` is a **parameter**, not an import, so this
 *    module can be exercised against a throwaway client in a test.
 */

/** How long the first reconnect waits, before jitter. */
const BASE_RECONNECT_DELAY_MS = 500;

/**
 * The cap on reconnect backoff.
 *
 * Ten seconds rather than a minute: the thing on the other end is usually a dev
 * server that just restarted or a laptop that just woke, and the cost of a
 * failed attempt is one refused TCP connection. A board that reconnects a
 * minute after the network returns reads as broken.
 */
const MAX_RECONNECT_DELAY_MS = 10_000;

/** Close code the server uses for every refusal (§ "1008 IS TERMINAL" above). */
const CLOSE_POLICY_VIOLATION = 1008;

/** Close code this client sends when the last caller unsubscribes. */
const CLOSE_NORMAL = 1000;

/**
 * Everything a subscriber can be told about its connection.
 *
 * `'refused'` is terminal and means 1008 — not permitted, or access revoked
 * mid-session, indistinguishably. Every other value is transient.
 */
export type BoardSubscriptionStatus =
  'connecting' | 'open' | 'reconnecting' | 'refused' | 'closed';

/**
 * A parsed inbound frame: either a persisted activity entry (`liveFrameSchema`,
 * which **is** `eventSchema` aliased) or one of `B61`'s three transient
 * non-activity frames.
 *
 * A consumer narrows on `type`, exactly as it would server-side.
 */
export type LiveSocketFrame = LiveFrame | LiveNonActivityFrame;

/** What {@link BoardSocketClient.subscribe} is given. */
export interface BoardSubscriptionRequest {
  /**
   * The cache to invalidate. A **parameter** rather than a module import so a
   * test can drive a throwaway client, and so this file never assumes there is
   * exactly one `QueryClient` in the process.
   */
  readonly queryClient: QueryClient;
  /** The board to subscribe to. Must be a ULID; see the class doc comment. */
  readonly boardId: string;
  /**
   * Told every time the connection's status changes, and once synchronously on
   * subscribe with the status as it stands. The place to notice `'refused'`.
   */
  readonly onStatusChange?: (status: BoardSubscriptionStatus) => void;
  /**
   * Every frame this subscriber's connection parsed, **after** invalidation has
   * been queued. `FE-18.1` is the intended caller: reconciling a frame against
   * optimistic local state needs the frame itself, not just the refetch this
   * module triggers. Unparseable frames never reach it.
   */
  readonly onFrame?: (frame: LiveSocketFrame) => void;
}

/** What {@link BoardSocketClient.subscribe} returns. */
export interface BoardSubscriptionHandle {
  /** The board this handle is subscribed to. */
  readonly boardId: string;
  /** The connection's status right now, for a caller that polls rather than listens. */
  readonly status: () => BoardSubscriptionStatus;
  /**
   * Leave. Idempotent. When the last subscriber for a board leaves, the socket
   * is closed and any pending reconnect timer is cancelled — a timer that
   * outlives its last listener reconnects to a board nothing is watching.
   */
  readonly unsubscribe: () => void;
}

interface Subscriber {
  readonly request: BoardSubscriptionRequest;
}

interface BoardConnection {
  readonly boardId: string;
  readonly subscribers: Set<Subscriber>;
  socket: WebSocket | null;
  status: BoardSubscriptionStatus;
  /**
   * Failed attempts since the last successful open. Reset to 0 on every open —
   * without that reset the second disconnect of a session starts at the cap.
   */
  attempt: number;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  /**
   * Whether this connection has ever been open. The first open of a connection
   * that has never failed does **not** invalidate: the caller's own queries are
   * loading at that moment, and there is no gap behind it to close.
   */
  everOpened: boolean;
  /** Set when the last subscriber leaves, so the close handler stops working. */
  disposed: boolean;
}

/**
 * The absolute `ws://` / `wss://` URL for a board room.
 *
 * Derived from `window.location`, so the client "never knows a second origin
 * exists" — the same discipline `./api`'s header states for `fetch`, applied to
 * a protocol where a relative URL is not portable enough to rely on. In
 * development Vite's `'/ws'` proxy answers it; in production Caddy does.
 *
 * Exported for the test that asserts the exact string, and for a reader who
 * wants to see the path without reading the class.
 */
export function boardSocketUrl(boardId: string): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  // No `cardId` parameter, ever — see "ONE SOCKET PER BOARD" above. Omitted
  // entirely rather than sent empty, which the server refuses with 1008.
  return `${protocol}//${window.location.host}/ws?boardId=${encodeURIComponent(boardId)}`;
}

/**
 * Decide which family a raw socket message belongs to, and parse it.
 *
 * ⚠ **`liveFrameSchema` is a `z.object`, so it STRIPS unknown keys rather than
 * refusing them** (`live.ts` names this trap explicitly). `safeParse().success`
 * is therefore *not* a test of "is this that family" — a typing frame would
 * fail it only by accident, and a future frame carrying an extra key would pass
 * it while losing the key. So the discriminator is `type` against the closed
 * `EVENT_TYPE_NAMES` vocabulary, checked **before** either parse, and the parse
 * is then a validation of a family already chosen.
 *
 * Returns `null` for anything unparseable — not a throw. This runs inside
 * `onmessage`, where an exception is an unhandled rejection in the console and
 * a socket that keeps running as though nothing happened; a dropped frame is
 * counted instead, and the next frame or the next reconnect resyncs the cache.
 */
function parseFrame(data: unknown): LiveSocketFrame | null {
  if (typeof data !== 'string') return null;

  let json: unknown;
  try {
    json = JSON.parse(data);
  } catch {
    return null;
  }

  if (typeof json !== 'object' || json === null) return null;

  const type: unknown = (json as { type?: unknown }).type;
  if (typeof type !== 'string') return null;

  const isActivityEntry = (EVENT_TYPE_NAMES as readonly string[]).includes(type);
  const parsed = isActivityEntry
    ? liveFrameSchema.safeParse(json)
    : liveNonActivityFrameSchema.safeParse(json);

  return parsed.success ? parsed.data : null;
}

/**
 * The query-key prefixes one frame makes stale.
 *
 * ⚠ **`'comment.typing'` makes nothing stale**, and this is the one place where
 * routing every frame through `eventBlastRadius` would be actively wrong:
 * typing is a transient socket fact that no cached response contains, and a
 * frame arrives per keystroke-ish. Invalidating the card on each would refetch
 * the card back continuously while somebody types a comment. It is still
 * delivered to `onFrame`, which is where `B61` will read it.
 */
function invalidationKeysFor(frame: LiveSocketFrame): MoltQueryKey[] {
  if (frame.type === 'comment.typing') return [];
  if (frame.type === 'card.mirror_source_changed') {
    return [queryKeys.boards.detail(frame.boardId)];
  }
  return eventBlastRadius({
    boardId: frame.boardId,
    cardId: frame.cardId,
  });
}

/**
 * The live board socket client.
 *
 * One instance owns the whole registry of board connections, which is what
 * makes the refcount work: two callers reach the same connection because they
 * went through the same client. The app shares {@link boardSockets}; a test
 * constructs its own and gets total isolation without this module needing a
 * reset hatch.
 *
 * @example
 * `FE-13.1`'s board face, in a `useEffect` keyed by `boardId`:
 *
 * ```ts
 * const queryClient = useQueryClient();
 *
 * useEffect(() => {
 *   const subscription = boardSockets.subscribe({
 *     queryClient,
 *     boardId,
 *     onStatusChange: (status) => setLive(status === 'open'),
 *   });
 *   return () => { subscription.unsubscribe(); };
 * }, [boardId, queryClient]);
 * ```
 */
export class BoardSocketClient {
  private readonly connections = new Map<string, BoardConnection>();

  /**
   * ⚠ On the **client**, not on the connection, and that is the whole point: a
   * connection is deleted from the registry when its last subscriber leaves, so
   * a per-connection tally would go *down* as boards unmount — reading "no
   * drift" precisely when the drift happened on a board that has since closed.
   * A counter that can decrease is worse than no counter.
   */
  private dropped = 0;

  /**
   * Join a board's live stream, opening the socket if this is the first caller.
   *
   * First intended callers: `FE-13.1`'s board face and `FE-14.x`'s card back —
   * two subscriptions, one connection. `FE-18.1` is the first caller of
   * `onFrame`.
   *
   * @throws if `boardId` is not a ULID. Synchronously and deliberately: the
   * server answers a malformed id with the same 1008 it uses for "not
   * permitted", so an unvalidated typo would surface to the caller as lost
   * access rather than as the bug it is.
   */
  subscribe(request: BoardSubscriptionRequest): BoardSubscriptionHandle {
    if (!ulidSchema.safeParse(request.boardId).success) {
      throw new Error(
        `boardId must be a ULID, received ${JSON.stringify(request.boardId)}`,
      );
    }

    const connection = this.connectionFor(request.boardId);
    const subscriber: Subscriber = { request };
    connection.subscribers.add(subscriber);

    // Synchronously, so a caller that binds a banner to the status does not
    // start out showing 'connecting' for a connection that is already open.
    request.onStatusChange?.(connection.status);

    let released = false;
    return {
      boardId: request.boardId,
      status: () => connection.status,
      unsubscribe: () => {
        if (released) return;
        released = true;
        connection.subscribers.delete(subscriber);
        if (connection.subscribers.size === 0) this.dispose(connection);
      },
    };
  }

  /**
   * How many frames this client dropped as unparseable, across every board.
   *
   * Counted rather than logged per frame: a schema drift would otherwise be a
   * silent nothing (`live.ts` explains why the server cannot validate on the
   * way out), and a console line per frame on a busy board is its own problem.
   * A test asserts on this; a future diagnostics surface can read it. It only
   * ever rises, for the life of the client — see {@link dropped}.
   */
  get droppedFrameCount(): number {
    return this.dropped;
  }

  private connectionFor(boardId: string): BoardConnection {
    const existing = this.connections.get(boardId);
    if (existing) {
      // `'refused'` is terminal for the connection, not for the board — see the
      // header. A newly arriving subscriber is the only new evidence this
      // client ever gets, so it buys one more attempt.
      if (existing.status === 'refused') {
        existing.attempt = 0;
        this.open(existing);
      }
      return existing;
    }

    const connection: BoardConnection = {
      boardId,
      subscribers: new Set<Subscriber>(),
      socket: null,
      status: 'connecting',
      attempt: 0,
      reconnectTimer: null,
      everOpened: false,
      disposed: false,
    };
    this.connections.set(boardId, connection);
    this.open(connection);
    return connection;
  }

  private open(connection: BoardConnection): void {
    this.setStatus(connection, connection.everOpened ? 'reconnecting' : 'connecting');

    const socket = new WebSocket(boardSocketUrl(connection.boardId));
    connection.socket = socket;

    socket.addEventListener('open', () => {
      if (connection.disposed) return;
      const closingAGap = connection.everOpened || connection.attempt > 0;
      connection.everOpened = true;
      // ⚠ Not optional: without it the second disconnect of a session starts
      // its backoff at the cap.
      connection.attempt = 0;
      this.setStatus(connection, 'open');
      // No replay exists, so the gap is invisible — see the header.
      if (closingAGap) this.invalidateAfterGap(connection);
    });

    socket.addEventListener('message', (event: MessageEvent) => {
      if (connection.disposed) return;
      this.handleMessage(connection, event.data);
    });

    socket.addEventListener('close', (event: CloseEvent) => {
      if (connection.disposed) return;
      if (connection.socket !== socket) return; // superseded by a newer attempt
      connection.socket = null;

      if (event.code === CLOSE_POLICY_VIOLATION) {
        // Terminal. Retrying a refusal earns the same refusal.
        this.setStatus(connection, 'refused');
        return;
      }

      this.scheduleReconnect(connection);
    });

    // The browser fires `error` *and* then `close` for a failed connection, so
    // reconnection is driven from `close` alone; without a listener here the
    // event surfaces as an unhandled error in the console.
    socket.addEventListener('error', () => {
      /* handled by the close listener that always follows */
    });
  }

  private handleMessage(connection: BoardConnection, data: unknown): void {
    const frame = parseFrame(data);
    if (frame === null) {
      this.dropped += 1;
      return;
    }

    const keys = invalidationKeysFor(frame);
    for (const queryClient of this.clientsOf(connection)) {
      for (const queryKey of keys) {
        // A **prefix** match, never `{ exact: true }` — see `eventBlastRadius`:
        // exact would match `['boards', id]` and miss `['boards', id,
        // 'contents']`, the entry the board face actually reads.
        void queryClient.invalidateQueries({ queryKey });
      }
    }

    for (const subscriber of connection.subscribers) {
      subscriber.request.onFrame?.(frame);
    }
  }

  /**
   * The reconnect sweep: deliberately coarser than a frame's blast radius,
   * because nothing says what the gap contained. See the header.
   */
  private invalidateAfterGap(connection: BoardConnection): void {
    for (const queryClient of this.clientsOf(connection)) {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.detail(connection.boardId),
      });
      // `['cards']`, not this board's cards: a frame names a card, a gap does
      // not, and `['boards', id]` as a prefix cannot reach `['cards', cardId]`.
      // Only active queries refetch, so this is one refetch per card back on
      // screen.
      void queryClient.invalidateQueries({ queryKey: queryKeys.cards.all() });
    }
  }

  /**
   * The distinct caches to invalidate.
   *
   * Deduplicated by identity because two callers on one board normally hold the
   * same `QueryClient` — invalidating it twice per frame would double every
   * refetch the socket causes.
   */
  private clientsOf(connection: BoardConnection): Set<QueryClient> {
    const clients = new Set<QueryClient>();
    for (const subscriber of connection.subscribers) {
      clients.add(subscriber.request.queryClient);
    }
    return clients;
  }

  private scheduleReconnect(connection: BoardConnection): void {
    this.setStatus(connection, 'reconnecting');

    // Capped exponential backoff with jitter. The jitter is the half of the
    // window below the cap: a server restarting under a room of twenty
    // subscribers should not be met by twenty reconnects in the same
    // millisecond, over and over, in lockstep.
    const capped = Math.min(
      MAX_RECONNECT_DELAY_MS,
      BASE_RECONNECT_DELAY_MS * 2 ** connection.attempt,
    );
    const delay = capped / 2 + Math.random() * (capped / 2);
    connection.attempt += 1;

    connection.reconnectTimer = setTimeout(() => {
      connection.reconnectTimer = null;
      if (connection.disposed) return;
      if (connection.subscribers.size === 0) return;
      this.open(connection);
    }, delay);
  }

  private setStatus(connection: BoardConnection, status: BoardSubscriptionStatus): void {
    if (connection.status === status) return;
    connection.status = status;
    for (const subscriber of connection.subscribers) {
      subscriber.request.onStatusChange?.(status);
    }
  }

  private dispose(connection: BoardConnection): void {
    connection.disposed = true;
    // By identity, not by id: deleting by key alone would let a stale handle's
    // late `unsubscribe()` evict a newer connection for the same board.
    if (this.connections.get(connection.boardId) === connection) {
      this.connections.delete(connection.boardId);
    }

    if (connection.reconnectTimer !== null) {
      // A pending timer that outlives its last subscriber reconnects to a board
      // nobody is watching, and in a test keeps the run alive.
      clearTimeout(connection.reconnectTimer);
      connection.reconnectTimer = null;
    }

    const socket = connection.socket;
    connection.socket = null;
    connection.status = 'closed';
    // 1000: a deliberate departure, not a failure. The server's `leave()` runs
    // off the close event either way.
    socket?.close(CLOSE_NORMAL);
  }
}

/**
 * The client the app shares.
 *
 * A single instance is what makes "one socket per board" true across
 * independently mounted callers — `FE-13.1`'s board face and `FE-14.x`'s card
 * back import this, not the class. **No wiring in `main.tsx` is needed or
 * wanted**: the `QueryClient` arrives per subscription from the caller's own
 * `useQueryClient()`, so there is nothing for the entry point to provide.
 *
 * A test constructs its own {@link BoardSocketClient} instead, which is why
 * this module ships no reset hatch.
 */
export const boardSockets = new BoardSocketClient();
